import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const platformLinks = [
  { label: 'Community Directory', href: '/' },
  { label: 'Submit a Community', href: '#submit', isSubmit: true },
  { label: 'Advertise With Us', to: '/contact' },
  { label: 'API Access', href: '#' },
  { label: 'Partner Program', href: '#' },
  { label: 'Featured Listings', href: '#' },
];

const rankingLinks = [
  { label: 'Top Monthly', key: 'monthly' },
  { label: 'Top by Categories', key: 'categories' },
  { label: 'Top by Regions', key: 'regions' },
  { label: 'Top by Source', key: 'source' },
  { label: 'Top by Revenue', key: 'revenue' },
];

const supportLinks = [
  { label: 'Blog & Guides', to: '/blogs' },
  { label: 'Contact Us', to: '/contact' },
  { label: 'Report a Community', href: '#' },
  { label: 'Community Guidelines', href: '#' },
  { label: 'Privacy Policy', href: '#' },
  { label: 'Terms of Service', href: '#' },
  { label: 'Cookie Policy', href: '#' },
  { label: 'Status Page', href: '#' },
];

export default function Footer() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Only show active letter when on home page
  const isHomePage = window.location.pathname === '/' || window.location.pathname === '';
  const activeLetter = isHomePage ? (searchParams.get('letter')?.toUpperCase() ?? null) : null;
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubStatus('loading');
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch('https://readdy.ai/api/form/d7evrrsq2rhf4jj5qaqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubStatus('success');
        setEmail('');
      } else {
        setSubStatus('error');
      }
    } catch {
      setSubStatus('error');
    }
  };

  const handleSubmitLink = (e: React.MouseEvent) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('open-submit-modal'));
  };

  return (
    <footer className="bg-[#f0f5fa] border-t border-slate-200/70 mt-12">
      {/* Newsletter banner */}
      <div className="border-b border-slate-200/50">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0088CC]/12 border border-[#0088CC]/20 shrink-0">
                <i className="ri-mail-send-line text-[#0088CC] text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-slate-700">Weekly Community Picks</p>
                <p className="text-xs text-slate-400">Get the best new Telegram communities every Monday. No spam.</p>
              </div>
            </div>
            <form
              data-readdy-form
              onSubmit={handleSubscribe}
              className="flex gap-0 w-full sm:w-auto"
            >
              {subStatus === 'success' ? (
                <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium px-4 py-2.5 bg-emerald-50 border border-emerald-200/60 rounded-full">
                  <i className="ri-check-line"></i>
                  Subscribed!
                </div>
              ) : (
                <>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="flex-1 sm:w-56 bg-white border border-slate-200 border-r-0 text-sm text-slate-700 placeholder-slate-400 px-4 py-2.5 rounded-l-full outline-none focus:border-[#0088CC]/50 transition-colors min-w-0"
                  />
                  <button
                    type="submit"
                    disabled={subStatus === 'loading'}
                    className="bg-[#0088CC] hover:bg-[#0077bb] text-white text-sm font-semibold px-5 py-2.5 rounded-r-full transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {subStatus === 'loading' ? 'Sending...' : 'Subscribe'}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.8fr_1fr_1fr_1fr] gap-10 xl:gap-14">

          {/* Brand col */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <img
                src="https://public.readdy.ai/ai/img_res/d5d862b0-845f-46d1-8b65-431c8335c5b6.png"
                alt="TGDirectory"
                className="w-8 h-8 object-contain"
              />
              <span className="text-slate-800 font-bold text-lg">
                TG<span className="text-[#0088CC]">Directory</span>
              </span>
            </div>
            <p className="text-slate-500 text-sm leading-relaxed mb-5">
              The largest Telegram community directory. Discover, join, and grow communities that matter to you.
            </p>
            <div className="flex gap-2.5 mb-6">
              {[
                { icon: 'ri-telegram-fill', label: 'Telegram' },
                { icon: 'ri-twitter-x-fill', label: 'X / Twitter' },
                { icon: 'ri-github-fill', label: 'GitHub' },
                { icon: 'ri-discord-fill', label: 'Discord' },
              ].map(({ icon, label }) => (
                <a
                  key={icon}
                  href="#"
                  aria-label={label}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/70 hover:bg-white border border-[#b8d4ea]/50 hover:border-[#0088CC]/30 text-slate-400 hover:text-[#0088CC] transition-all cursor-pointer"
                >
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>

            {/* Browse A–Z compact inside brand col on mobile, full section on desktop */}
            <div>
              <h4 id="browse-az" className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                <i className="ri-sort-asc text-[#0088CC] text-sm"></i>
                Browse Communities A–Z
              </h4>
              <div className="flex flex-wrap gap-1">
                {alphabet.map((letter) => {
                const isActive = activeLetter === letter;
                return (
                  <button
                    key={letter}
                    onClick={() => {
                      const newLetter = isActive ? null : letter;
                      if (!isHomePage) {
                        // Navigate to home with letter param
                        navigate(newLetter ? `/?letter=${newLetter}` : '/');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        return;
                      }
                      if (newLetter === null) {
                        searchParams.delete('letter');
                        setSearchParams(searchParams, { replace: true });
                      } else {
                        setSearchParams({ letter: newLetter }, { replace: true });
                      }
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`w-6 h-6 flex items-center justify-center text-[11px] font-bold rounded transition-all cursor-pointer ${
                      isActive
                        ? 'bg-[#0088CC] text-white'
                        : 'bg-white/80 text-slate-500 hover:bg-[#0088CC]/10 hover:text-[#0088CC] border border-[#c5d9eb]/60'
                    }`}
                  >
                    {letter}
                  </button>
                );
              })}
              </div>
            </div>
          </div>

          {/* Platform */}
          <div>
            <h4 id="platform" className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
              <i className="ri-apps-2-line text-[#0088CC] text-sm"></i>
              Platform
            </h4>
            <ul className="flex flex-col gap-2.5">
              {platformLinks.map((link) => {
                if (link.to) {
                  return (
                    <li key={link.label}>
                      <Link
                        to={link.to}
                        className="text-sm text-slate-400 hover:text-[#0088CC] transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  );
                }
                if (link.isSubmit) {
                  return (
                    <li key={link.label}>
                      <a
                        href="#submit"
                        onClick={handleSubmitLink}
                        className="text-sm text-slate-400 hover:text-[#0088CC] transition-colors cursor-pointer"
                      >
                        {link.label}
                      </a>
                    </li>
                  );
                }
                return (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      rel="nofollow"
                      className="text-sm text-slate-400 hover:text-[#0088CC] transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Rankings */}
          <div>
            <h4 id="rankings" className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
              <i className="ri-bar-chart-grouped-line text-[#0088CC] text-sm"></i>
              Rankings
            </h4>
            <ul className="flex flex-col gap-2.5">
              {rankingLinks.map((item) => (
                <li key={item.key}>
                  <button
                    onClick={() => navigate(`/ranking/${item.key}`)}
                    className="text-sm text-slate-400 hover:text-[#0088CC] transition-colors cursor-pointer text-left"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => navigate('/blogs?category=Rankings')}
                  className="text-sm text-slate-400 hover:text-[#0088CC] transition-colors cursor-pointer text-left"
                >
                  Ranking Reports
                </button>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 id="support" className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
              <i className="ri-customer-service-2-line text-[#0088CC] text-sm"></i>
              Support
            </h4>
            <ul className="flex flex-col gap-2.5">
              {supportLinks.map((link) => {
                if (link.to) {
                  return (
                    <li key={link.label}>
                      <Link
                        to={link.to}
                        className="text-sm text-slate-400 hover:text-[#0088CC] transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  );
                }
                return (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      rel="nofollow"
                      className="text-sm text-slate-400 hover:text-[#0088CC] transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-200/60 px-6 md:px-10 lg:px-14 py-4">
        <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-slate-400 text-center sm:text-left">
            &copy; 2026 TGDirectory. All rights reserved.
          </p>
          <p className="text-xs text-slate-400">
            Built for Telegram community builders worldwide.
          </p>
        </div>
      </div>
    </footer>
  );
}
